import React, { useEffect, useRef, useState } from 'react';
import { usePS2Graphics } from '../context/PS2GraphicsContext';

/**
 * Premium animated background — Canvas-rendered night golf course.
 * 
 * Features:
 * - Canvas starfield with real twinkling + periodic shooting stars
 * - Layered SVG fairway hills with CSS parallax
 * - Animated flag pins with cloth physics
 * - Firefly particle system
 * - Periodic golf ball arc animation (tee shot!)
 * - Golf cart that drives across occasionally
 * - Floating whimsical emojis
 * - Moon with atmospheric halo
 * - Drifting clouds
 */
export default function AnimatedBackground() {
  const canvasRef = useRef(null);
  const [golfBall, setGolfBall] = useState(null);
  const [golfCart, setGolfCart] = useState(null);
  const { ps2Mode } = usePS2Graphics();

  // Canvas starfield + shooting stars
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let w, h;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Stars
    const stars = Array.from({ length: 120 }, () => ({
      x: Math.random() * 2000,
      y: Math.random() * 1000,
      r: Math.random() * 1.5 + 0.3,
      twinkleSpeed: Math.random() * 0.02 + 0.005,
      twinkleOffset: Math.random() * Math.PI * 2,
      maxOpacity: Math.random() * 0.5 + 0.15,
    }));

    // Shooting stars
    let shootingStars = [];
    const spawnShootingStar = () => {
      shootingStars.push({
        x: Math.random() * w * 0.6 + w * 0.1,
        y: Math.random() * h * 0.3,
        vx: (Math.random() * 3 + 2) * (Math.random() > 0.5 ? 1 : -1),
        vy: Math.random() * 2 + 1,
        life: 1,
        decay: Math.random() * 0.015 + 0.01,
        length: Math.random() * 40 + 20,
      });
    };

    let t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      t += 1;

      // Draw stars
      stars.forEach(s => {
        const opacity = s.maxOpacity * (0.5 + 0.5 * Math.sin(t * s.twinkleSpeed + s.twinkleOffset));
        ctx.beginPath();
        ctx.arc(s.x % w, s.y % h, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,240,${opacity})`;
        ctx.fill();

        // Star glow
        if (s.r > 1) {
          ctx.beginPath();
          ctx.arc(s.x % w, s.y % h, s.r * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,240,${opacity * 0.15})`;
          ctx.fill();
        }
      });

      // Shooting stars
      shootingStars.forEach(ss => {
        ctx.beginPath();
        ctx.moveTo(ss.x, ss.y);
        ctx.lineTo(ss.x - ss.vx * ss.length * ss.life, ss.y - ss.vy * ss.length * ss.life * 0.3);
        const grad = ctx.createLinearGradient(
          ss.x, ss.y,
          ss.x - ss.vx * ss.length * ss.life, ss.y - ss.vy * ss.length * ss.life * 0.3
        );
        grad.addColorStop(0, `rgba(255,255,255,${ss.life * 0.8})`);
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ss.x += ss.vx;
        ss.y += ss.vy;
        ss.life -= ss.decay;
      });
      shootingStars = shootingStars.filter(ss => ss.life > 0);

      // Spawn shooting star occasionally
      if (Math.random() < 0.003) spawnShootingStar();

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  // Golf ball arc — fires every 20-40 seconds
  useEffect(() => {
    const fireGolfBall = () => {
      const fromLeft = Math.random() > 0.5;
      setGolfBall({
        id: Date.now(),
        fromLeft,
        startX: fromLeft ? -5 : 105,
        endX: fromLeft ? 105 : -5,
        peakY: Math.random() * 20 + 10,
        duration: Math.random() * 2 + 2.5,
      });
      setTimeout(() => setGolfBall(null), 5000);
    };

    const interval = setInterval(fireGolfBall, Math.random() * 20000 + 20000);
    // First one after 8 seconds
    const firstTimeout = setTimeout(fireGolfBall, 8000);
    return () => { clearInterval(interval); clearTimeout(firstTimeout); };
  }, []);

  // Golf cart — drives across every 45-90 seconds
  useEffect(() => {
    const driveCart = () => {
      const fromLeft = Math.random() > 0.5;
      setGolfCart({
        id: Date.now(),
        fromLeft,
        duration: Math.random() * 8 + 12,
      });
      setTimeout(() => setGolfCart(null), 25000);
    };

    const interval = setInterval(driveCart, Math.random() * 45000 + 45000);
    const firstTimeout = setTimeout(driveCart, 15000);
    return () => { clearInterval(interval); clearTimeout(firstTimeout); };
  }, []);

  return (
    <>
      {/* Night sky base gradient */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background: ps2Mode
          ? 'linear-gradient(180deg, #020408 0%, #040a18 20%, #061028 40%, #081430 60%, #060e20 80%, #030610 100%)'
          : 'linear-gradient(180deg, #040a04 0%, #071508 20%, #0a1a0d 40%, #0d1f10 60%, #0a180c 80%, #060e06 100%)',
      }} />

      {/* Canvas — stars + shooting stars */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0"
        style={{ opacity: 0.9 }}
      />

      {/* Moon with atmospheric halo */}
      <div className="fixed pointer-events-none z-0" style={{
        right: '10%', top: '6%',
        width: '100px', height: '100px',
      }}>
        {/* Outer halo */}
        <div className="absolute -inset-8 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(255,255,220,0.04) 0%, rgba(255,255,220,0.015) 40%, transparent 70%)',
        }} />
        {/* Moon body */}
        <div className="absolute inset-0 rounded-full" style={{
          background: 'radial-gradient(circle at 35% 35%, rgba(255,255,235,0.15), rgba(255,255,220,0.06) 50%, rgba(255,255,200,0.02) 80%, transparent)',
          boxShadow: '0 0 80px 30px rgba(255,255,200,0.025)',
        }} />
        {/* Craters (subtle) */}
        <div className="absolute rounded-full" style={{
          width: '15px', height: '15px', top: '30%', left: '50%',
          background: 'radial-gradient(circle, rgba(0,0,0,0.08), transparent 70%)',
        }} />
        <div className="absolute rounded-full" style={{
          width: '10px', height: '10px', top: '55%', left: '35%',
          background: 'radial-gradient(circle, rgba(0,0,0,0.06), transparent 70%)',
        }} />
      </div>

      {/* Drifting clouds — 3 layers */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {[
          { top: '12%', width: '45%', height: '50px', opacity: 0.025, dur: 100, delay: 0, blur: 10 },
          { top: '22%', width: '35%', height: '35px', opacity: 0.018, dur: 140, delay: 30, blur: 14 },
          { top: '8%', width: '25%', height: '30px', opacity: 0.02, dur: 80, delay: 50, blur: 8 },
          { top: '30%', width: '55%', height: '40px', opacity: 0.012, dur: 180, delay: 70, blur: 18 },
        ].map((cloud, i) => (
          <div key={`cloud-${i}`} className="absolute" style={{
            top: cloud.top, left: '-20%', width: cloud.width, height: cloud.height,
            background: `linear-gradient(90deg, transparent, rgba(255,255,255,${cloud.opacity}) 30%, rgba(255,255,255,${cloud.opacity * 1.4}) 50%, rgba(255,255,255,${cloud.opacity}) 70%, transparent)`,
            borderRadius: '50%',
            animation: `cloud-drift ${cloud.dur}s linear ${cloud.delay}s infinite`,
            filter: `blur(${cloud.blur}px)`,
          }} />
        ))}
      </div>

      {/* Fairway hills — 4 SVG layers */}
      <div className="fixed bottom-0 left-0 right-0 pointer-events-none z-0" style={{ height: '65%' }}>
        {/* Treeline */}
        <svg className="absolute bottom-0 w-full" style={{ height: '100%' }} viewBox="0 0 1440 500" preserveAspectRatio="none">
          <path d="M0 500 L0 340 Q60 310 90 330 Q120 300 150 320 Q180 290 210 310 Q250 280 280 305 Q320 270 360 295 Q400 260 440 285 Q480 255 520 280 Q560 250 600 275 Q640 245 680 270 Q720 240 760 265 Q800 235 840 260 Q880 230 920 255 Q960 220 1000 250 Q1040 225 1080 245 Q1120 215 1160 240 Q1200 210 1240 235 Q1280 205 1320 230 Q1360 200 1400 225 L1440 220 L1440 500 Z"
            fill={ps2Mode ? 'url(#treeline-ps2)' : 'url(#treeline)'} opacity="0.55" />
          <defs>
            <linearGradient id="treeline" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#14532d" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#052e16" stopOpacity="0.15" />
            </linearGradient>
            <linearGradient id="treeline-ps2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e3a5f" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#0a1628" stopOpacity="0.15" />
            </linearGradient>
          </defs>
        </svg>
        {/* Far hill */}
        <svg className="absolute bottom-0 w-full" style={{ height: '85%' }} viewBox="0 0 1440 400" preserveAspectRatio="none">
          <path d="M0 400 L0 280 Q180 200 360 250 Q540 300 720 220 Q900 140 1080 200 Q1260 260 1440 180 L1440 400 Z"
            fill={ps2Mode ? 'url(#hill-far-ps2)' : 'url(#hill-far)'} opacity="0.65" />
          <defs>
            <linearGradient id="hill-far" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#166534" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#052e16" stopOpacity="0.12" />
            </linearGradient>
            <linearGradient id="hill-far-ps2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e40af" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#0a1628" stopOpacity="0.12" />
            </linearGradient>
          </defs>
        </svg>
        {/* Mid hill */}
        <svg className="absolute bottom-0 w-full" style={{ height: '65%' }} viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path d="M0 320 L0 200 Q200 140 400 180 Q600 220 800 160 Q1000 100 1200 170 Q1350 210 1440 180 L1440 320 Z"
            fill={ps2Mode ? 'url(#hill-mid-ps2)' : 'url(#hill-mid)'} opacity="0.75" />
          <defs>
            <linearGradient id="hill-mid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#15803d" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#052e16" stopOpacity="0.1" />
            </linearGradient>
            <linearGradient id="hill-mid-ps2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1d4ed8" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#0a1628" stopOpacity="0.1" />
            </linearGradient>
          </defs>
        </svg>
        {/* Near hill */}
        <svg className="absolute bottom-0 w-full" style={{ height: '35%' }} viewBox="0 0 1440 200" preserveAspectRatio="none">
          <path d="M0 200 L0 100 Q240 60 480 90 Q720 120 960 80 Q1200 50 1440 75 L1440 200 Z"
            fill={ps2Mode ? 'url(#hill-near-ps2)' : 'url(#hill-near)'} opacity="0.85" />
          <defs>
            <linearGradient id="hill-near" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#16a34a" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#052e16" stopOpacity="0.08" />
            </linearGradient>
            <linearGradient id="hill-near-ps2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2563eb" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#0a1628" stopOpacity="0.08" />
            </linearGradient>
          </defs>
        </svg>

        {/* Flag pins with waving flags */}
        {[
          { left: '18%', bottom: '42%', height: 75, flagColor: '#ef4444', opacity: 0.35 },
          { left: '48%', bottom: '25%', height: 60, flagColor: '#eab308', opacity: 0.25 },
          { left: '78%', bottom: '36%', height: 55, flagColor: '#3b82f6', opacity: 0.22 },
        ].map((pin, i) => (
          <div key={`pin-${i}`} className="absolute" style={{ left: pin.left, bottom: pin.bottom, opacity: pin.opacity }}>
            <div style={{ width: '1.5px', height: `${pin.height}px`, background: 'linear-gradient(to top, rgba(255,255,255,0.35), rgba(255,255,255,0.08))', borderRadius: '1px' }} />
            <div style={{
              position: 'absolute', top: '0', left: '1.5px', width: '16px', height: '10px',
              background: pin.flagColor, opacity: 0.8,
              clipPath: 'polygon(0 0, 100% 35%, 0 100%)',
              animation: `flag-wave 2.5s ease-in-out ${i * 0.3}s infinite`,
              transformOrigin: 'left center',
            }} />
            <div style={{
              position: 'absolute', bottom: '-3px', left: '-8px', width: '18px', height: '6px',
              background: ps2Mode
                ? 'radial-gradient(ellipse, rgba(6,182,212,0.25), transparent)'
                : 'radial-gradient(ellipse, rgba(34,197,94,0.25), transparent)',
              borderRadius: '50%',
            }} />
          </div>
        ))}

        {/* Sand traps / bunkers */}
        {[
          { left: '30%', bottom: '18%', width: 50, height: 14, opacity: 0.18 },
          { left: '62%', bottom: '22%', width: 35, height: 10, opacity: 0.14 },
          { left: '85%', bottom: '15%', width: 40, height: 12, opacity: 0.12 },
        ].map((trap, i) => (
          <div key={`trap-${i}`} className="absolute" style={{
            left: trap.left, bottom: trap.bottom, width: `${trap.width}px`, height: `${trap.height}px`,
            background: 'radial-gradient(ellipse, rgba(217,179,120,0.4), rgba(194,154,90,0.15) 60%, transparent 100%)',
            borderRadius: '50%', opacity: trap.opacity,
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          }} />
        ))}

        {/* Water hazard */}
        <div className="absolute" style={{
          left: '42%', bottom: '10%', width: '80px', height: '20px',
          background: 'radial-gradient(ellipse, rgba(56,189,248,0.15), rgba(14,165,233,0.06) 60%, transparent)',
          borderRadius: '50%', opacity: 0.5,
          animation: 'firefly-glow 6s ease-in-out infinite',
        }} />
      </div>

      {/* Firefly system */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {Array.from({ length: 25 }, (_, i) => (
          <div
            key={`fly-${i}`}
            className="absolute rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${40 + Math.random() * 55}%`,
              width: `${Math.random() * 3 + 1.5}px`,
              height: `${Math.random() * 3 + 1.5}px`,
              background: ps2Mode
                ? `radial-gradient(circle, rgba(147,197,253,0.7), rgba(147,197,253,0.1))`
                : `radial-gradient(circle, rgba(250,240,137,0.7), rgba(250,240,137,0.1))`,
              boxShadow: ps2Mode
                ? `0 0 ${Math.random() * 6 + 3}px rgba(147,197,253,0.3)`
                : `0 0 ${Math.random() * 6 + 3}px rgba(250,240,137,0.3)`,
              animation: `firefly-glow ${Math.random() * 5 + 3}s ease-in-out ${Math.random() * 8}s infinite, emoji-float ${Math.random() * 30 + 20}s ease-in-out ${Math.random() * 10}s infinite`,
            }}
          />
        ))}
      </div>



      {/* Golf ball arc animation */}
      {golfBall && (
        <div
          key={golfBall.id}
          className="fixed pointer-events-none z-[1]"
          style={{
            animation: `golf-shot-x ${golfBall.duration}s linear forwards`,
            left: golfBall.fromLeft ? '0%' : 'auto',
            right: golfBall.fromLeft ? 'auto' : '0%',
          }}
        >
          <div style={{
            animation: `golf-shot-y ${golfBall.duration}s ease-in-out forwards`,
            position: 'relative',
          }}>
            {/* Ball */}
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 35%, #fff, #e5e5e5)',
              boxShadow: '0 0 12px rgba(255,255,255,0.4), 0 0 4px rgba(255,255,255,0.8)',
              animation: `golf-ball-spin ${golfBall.duration * 0.3}s linear infinite`,
            }} />
            {/* Trail */}
            <div style={{
              position: 'absolute', top: '3px', right: '8px', width: '30px', height: '2px',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15))',
              borderRadius: '1px',
              transform: golfBall.fromLeft ? 'scaleX(-1)' : 'none',
            }} />
          </div>
        </div>
      )}

      {/* Golf cart easter egg */}
      {golfCart && (
        <div
          key={golfCart.id}
          className="fixed pointer-events-none z-0 select-none"
          style={{
            bottom: '8%',
            animation: `${golfCart.fromLeft ? 'cart-drive-right' : 'cart-drive-left'} ${golfCart.duration}s linear forwards`,
            fontSize: '22px',
            opacity: 0.12,
            filter: 'blur(0.3px)',
          }}
        >
          🏎️
        </div>
      )}

      {/* Atmospheric glows */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_85%_8%,rgba(255,255,200,0.025),transparent_35%)] pointer-events-none z-0" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_50%_85%,rgba(34,197,94,0.05),transparent_45%)] pointer-events-none z-0" />

      {/* Vignette */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background: 'radial-gradient(ellipse at 50% 50%, transparent 45%, rgba(0,0,0,0.5) 100%)',
      }} />
    </>
  );
}
